import { Formik, FormikHelpers, FormikProps } from 'formik'
import * as Yup from 'yup'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'

import toast from 'react-hot-toast'
import { FILE_SIZE, SUPPORTED_FORMATS } from '../../utils/config'
import customerService from '../../services/customerService'
import { useMutation } from 'react-query'
import { queryClient } from '../../utils/react-query'
import { Customer, NewCustomerTDO } from '../../types/customer'
import CustomerForm from './CustomerForm'
import { BuzzlUsersError } from '../../types/buzzl-user'

const schema = Yup.object().shape({
  name: Yup.string().required(),
  adminEmail: Yup.string().email().required(),
  fallbackDescription: Yup.string().required(),
  fallbackActionText: Yup.string().required(),
  fallbackActionUrl: Yup.string().url().required(),
  fallbackImage: Yup.mixed()
    .test('fileSize', 'File too large', (value) => !value || (value && value.size <= FILE_SIZE))
    .test('fileFormat', 'Unsupported Format', (value) => !value || (value && SUPPORTED_FORMATS.includes(value.type))),
  avatar: Yup.mixed()
    .test('fileSize', 'File too large', (value) => !value || (value && value.size <= FILE_SIZE))
    .test('fileFormat', 'Unsupported Format', (value) => !value || (value && SUPPORTED_FORMATS.includes(value.type))),
  totalLicenses: Yup.number().required(),
  places: Yup.array().of(
    Yup.object().shape({
      googlePlaceId: Yup.string(),
      description: Yup.string(),
    })
  ),
})

const Details = () => {
  const navigate = useNavigate()
  const { customerId } = useParams<{ customerId: string }>()
  const { data } = useOutletContext<{ data: Customer }>()

  const createCustomer = useMutation(customerService.createCustomer, {
    onSuccess: async () => {
      queryClient.refetchQueries('customers')
      toast.success('Customer created.')
      navigate('/customers')
    },
    onError: (e) => {
      const error = e as BuzzlUsersError
      error.response.errors.map((er) =>
        toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
      )
    },
  })

  const updateCustomer = useMutation(customerService.updateCustomer, {
    onSuccess: async () => {
      queryClient.refetchQueries('customers')
      queryClient.refetchQueries('customer')
      toast.success('Customer Updated.')
      navigate('/customers')
    },
    onError: (e) => {
      const error = e as BuzzlUsersError
      error.response.errors.map((er) =>
        toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
      )
    },
  })

  const initialValues: NewCustomerTDO = {
    name: data?.name || '',
    adminEmail: data?.adminEmail || '',
    fallbackDescription: data?.fallbackDescription || '',
    fallbackActionText: data?.fallbackActionText || '',
    fallbackActionUrl: data?.fallbackActionUrl || '',
    fallbackImage: undefined,
    avatar: undefined,
    totalLicenses: data?.totalLicenses || 0,
    places: data?.claimedPlaces || [],
  }

  const handleOnSubmit = async (
    { avatar, fallbackImage, places, ...rest }: NewCustomerTDO,
    formikBag: FormikHelpers<NewCustomerTDO>
  ) => {
    // Check images required if is a new customer
    // This check its done firstly to prevent upload of only one image
    if (!customerId) {
      if (!avatar) {
        formikBag.setFieldError('avatar', 'Avatar is required.')
      }
      if (!fallbackImage) {
        formikBag.setFieldError('fallbackImage', 'Avatar is required.')
      }

      if (!avatar || !fallbackImage) {
        return null
      }
    }

    const obj: any = {
      ...rest,
    }

    if (avatar) {
      obj.avatar = await customerService.uploadImage({
        file: avatar,
      })
    }

    if (fallbackImage) {
      obj.fallbackImage = await customerService.uploadImage({
        file: fallbackImage,
      })
    }

    if (customerId) {
      // Update
      await updateCustomer.mutate({ ...obj, id: customerId })
    } else {
      // Create
      await createCustomer.mutate({ ...obj, places })
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={schema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={handleOnSubmit}
    >
      {({ setFieldValue, isSubmitting, values, dirty }: FormikProps<NewCustomerTDO>) => {
        return (
          <CustomerForm
            isSubmitting={isSubmitting}
            setFieldValue={setFieldValue}
            values={values}
            currentAvatar={data?.avatar?.url}
            currentFallbackImage={data?.fallbackImage?.url}
            isDisabled={isSubmitting || !dirty}
          />
        )
      }}
    </Formik>
  )
}

export default Details
